import { useState, useEffect } from 'react';
import { useColorContext } from '../contexts/ColorContext';
import { usePaletteContext } from '../contexts/PaletteContext';
import { GradientBuilderPanel } from '../components/GradientBuilderPanel';
import { PalettePicker } from '../components/PalettePicker';
import { useToolInit } from '../hooks/useToolInit';
import { Palette, Pipette } from 'lucide-react';

type GradientMode = 'freeform' | 'palette';

export function GradientTool() {
  const { result, setInput } = useColorContext();
  const { currentPalette, updateCurrentPalette } = usePaletteContext();
  const { params, state } = useToolInit();

  // Mode from URL or default based on palette availability
  const [mode, setMode] = useState<GradientMode>(
    (params.mode === 'freeform' || params.mode === 'palette')
      ? params.mode
      : currentPalette ? 'palette' : 'freeform'
  );

  // Deep-link: ?color= sets global active color
  useEffect(() => {
    if (params.color) setInput(params.color);
  }, [params.color, setInput]);

  // Pre-seeded colors from route state (e.g. from Extract → Gradient)
  const preSeededColors = state.colors as string[] | undefined;

  return (
    <div className="w-full max-w-4xl mx-auto p-6 space-y-4">
      {/* Mode switcher */}
      <div style={{
        display: 'flex', gap: '4px', padding: '3px',
        background: 'var(--panel-2, #f3f4f6)', borderRadius: 'var(--radius-lg, 8px)',
        width: 'fit-content',
      }}>
        <button
          type="button"
          onClick={() => setMode('freeform')}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 14px', borderRadius: 'var(--radius, 6px)',
            fontSize: '13px', fontWeight: 500, border: 'none', cursor: 'pointer',
            background: mode === 'freeform' ? 'var(--panel, #fff)' : 'transparent',
            color: mode === 'freeform' ? 'var(--ink, #111)' : 'var(--ink-3, #999)',
            boxShadow: mode === 'freeform' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <Pipette size={14} />
          Freeform
        </button>
        <button
          type="button"
          onClick={() => setMode('palette')}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 14px', borderRadius: 'var(--radius, 6px)',
            fontSize: '13px', fontWeight: 500, border: 'none', cursor: 'pointer',
            background: mode === 'palette' ? 'var(--panel, #fff)' : 'transparent',
            color: mode === 'palette' ? 'var(--ink, #111)' : 'var(--ink-3, #999)',
            boxShadow: mode === 'palette' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          <Palette size={14} />
          From Palette
        </button>
      </div>

      {/* Freeform mode: use current color + white as defaults, no palette needed */}
      {mode === 'freeform' && (
        <GradientBuilderPanel
          paletteName="Custom Gradient"
          paletteColors={preSeededColors ?? [result?.hex ?? '#3b82f6', '#ffffff']}
          onApplyToPalette={async () => {}}
        />
      )}

      {/* Palette mode: existing behavior with palette picker gate */}
      {mode === 'palette' && !currentPalette && (
        <PalettePicker prompt="Select a palette to build a gradient from its colors." />
      )}
      {mode === 'palette' && currentPalette && (
        <GradientBuilderPanel
          paletteName={currentPalette.name}
          paletteColors={currentPalette.colors}
          onApplyToPalette={async (colors) => {
            await updateCurrentPalette({ colors });
          }}
        />
      )}
    </div>
  );
}
